import useFluxStore from "@armus/use-flux-store";
import SearchFormActions from "data/SearchFormActions";
import SearchFormStore, { extractSearchToSave } from "data/SearchFormStore";
import StereotypesStore from "data/StereotypesStore";
import userStore from "data/userStore";
import ContextActions from "data/util/ContextActions";
import Dispatcher from "Dispatcher";
import Select from "lib/common/controls/Select";
import SearchForm from "lib/dataentry/lib/search/views/SearchForm";
import { title as genericXmlExportPasswordAdminTitle } from "lib/genericXmlExportPassword/GenericXmlExportPasswordAdmin";
import PropTypes from "prop-types";
import React from "react";

const fluxContext = "CTX-export-generic-xml";

function FormView({
                      stereotypes,
                      selectedStereotype,
                      onStereotypeSelect,
                      isAvailable,
                      isAdmin,
                      onShowAdmin,
                  }) {
    let header, body;
    if (isAvailable) {
        header = stereotypes.length === 1
            ? ("Export By " + stereotypes[0].name)
            : <Select
                id="stereotype"
                options={stereotypes.map(s => ({
                    value: s.key,
                    label: (s.key === selectedStereotype ? "Export By " : "") + s.name,
                }))}
                onCommitChange={onStereotypeSelect}
                value={selectedStereotype}
                label="Search By"
            />;
        body = <SearchForm
            fluxContext={fluxContext}
            onSubmit={console.log}
            alwaysShowAdvanced
        />;
    } else {
        header = "No Password!";
        body = [<p key="nope">
            Your organization doesn't have a password configured to secure
            ARMUS XML Exports, so they are currently disabled.
        </p>];
        if (isAdmin) {
            body.push(<p key="admin">
                As an administrator, you can set one via the <button
                className={"btn btn-sm btn-dark"}
                    onClick={e => {
                        e.preventDefault();
                        onShowAdmin();
                    }}
                >
                    {genericXmlExportPasswordAdminTitle}
                </button>.
            </p>);
        }
    }
    return <div className="form-section form-section-single generic-xml-export">
        <div className="form-section-header">
            {header}
        </div>
        <div className="form-section-inner">
            {body}
        </div>
    </div>;
}

FormView.propTypes = {
    stereotypes: PropTypes.arrayOf(
        PropTypes.shape({
            key: PropTypes.string.isRequired,
            name: PropTypes.string.isRequired,
        })).isRequired,
    selectedStereotype: PropTypes.string.isRequired,
    onStereotypeSelect: PropTypes.func.isRequired,
    onShowAdmin: PropTypes.func.isRequired,
    isAvailable: PropTypes.bool.isRequired,
    isAdmin: PropTypes.bool.isRequired,
};

const GenericXmlExportForm = props => {
    const { initialState: params, onReady, onNotReady } = props;
    const state = useFluxStore(() => {
        const stereotypes = StereotypesStore.getDataQualityStereotypes();
        const selectedStereotype = SearchFormStore.getKey(fluxContext);
        const onStereotypeSelect = (key, copyExisting) =>
            Dispatcher.contextDispatch(fluxContext, {
                type: SearchFormActions.SELECT_STEREOTYPE,
                fields: StereotypesStore.getStereotypeFieldsByKey(key),
                copyExisting,
                key,
            });
        // Calling back to on[Not]Ready synchronously is bad, but we can't hoist
        // state at the moment, since it lives in a Flux store. Once we can get
        // to Context, that'll magically fix itself.
        //
        // eslint-disable-next-line no-settimeout
        setTimeout(() => {
            if (SearchFormStore.hasValues(fluxContext)) {
                onReady({
                    // these are used for processing
                    s: selectedStereotype,
                    q: SearchFormStore.getQ(fluxContext).toAS(),
                    // these are used for labeling ('h' for 'human')
                    hq: SearchFormStore.getHuman(fluxContext),
                    hs: StereotypesStore.getStereotypeByKey(selectedStereotype).name,
                    // this is used for rerunning
                    _form: JSON.stringify(extractSearchToSave(SearchFormStore.getState(fluxContext))),
                });
            } else {
                onNotReady();
            }
        });
        return {
            stereotypes,
            selectedStereotype,
            onStereotypeSelect,
            isAvailable: userStore.isGenericXmlExportAvailable(),
            isAdmin: userStore.isPrefManForAnything(),
        };
    }, [
        StereotypesStore,
        SearchFormStore,
        userStore,
    ], [onReady, onNotReady]);
    React.useEffect(
        () => {
            // Initialize the search context, either bare or from passed params.
            //
            // eslint-disable-next-line no-settimeout
            setTimeout(() => {
                const action = {
                    type: SearchFormActions.INITIALIZE_CONTEXT,
                    key: state.stereotypes[0].key,
                    availableKeys: state.stereotypes.map(s => s.key),
                };
                if (params && params._form) {
                    try {
                        action.query = JSON.parse(params._form);
                        action.key = action.query.key;
                    } catch (e) {
                        console.warn("Error inflating Generic XML Export form state: " + e, e);
                    }
                }
                Dispatcher.contextDispatch(fluxContext, action);
            });
            // and close it when we unmount
            return () =>
                Dispatcher.contextDispatch(fluxContext, {
                    type: ContextActions.CLOSE,
                });
        },
        [params],
    );
    return <FormView
        onShowAdmin={() =>
            Backbone.dispatcher.trigger("load:genericXmlPasswordAdmin")}
        {...props}
        {...state}
    />;
};

GenericXmlExportForm.propTypes = {
    initialState: PropTypes.shape({
        _form: PropTypes.string,
    }),
    onReady: PropTypes.func.isRequired,
    onNotReady: PropTypes.func.isRequired,
};

GenericXmlExportForm.canRerun = params =>
    params.hasOwnProperty("_form");

GenericXmlExportForm.apiPrefix = "generic-xml";

export default GenericXmlExportForm;



// WEBPACK FOOTER //
// ./app/lib/hex/views/GenericXmlExportForm.js